import { cn } from '@/shared/lib/cn';
import { useAuthStore } from '@/shared/stores/auth-store';
import { ArrowLeftRight, Bot, Check, Lock, Pencil, Pin, User } from 'lucide-react';
import { useEffect, useState } from 'react';
import { relativeTime } from '../lib/format';
import { anelDoAvatar } from '../lib/anel-do-canal';
import { isPendingContentHidden } from '../lib/pending-content';
import { useRascunhos } from '../rascunhos';
import type { TicketSummary } from '../types';

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

/** Formato comum dos badges do card (canal, departamento, agente). */
const BADGE =
  'inline-flex items-center overflow-hidden whitespace-nowrap rounded-sm px-[6px] py-[2px] font-semibold text-[10px] leading-[15px]';

interface Props {
  ticket: TicketSummary;
  selected: boolean;
  onClick: () => void;
  selectable?: boolean;
  checked?: boolean;
  onCheckChange?: () => void;
  /** Right-click no card — abre menu de atalhos (transferir/encerrar). */
  onContextMenu?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /** Conversa fixada no topo (aba Ativos) — mostra o pin preenchido no lugar
   *  do horário. Fixar/desafixar é SÓ pelo menu de contexto (botão direito). */
  pinned?: boolean | undefined;
}

export function TicketCard({
  ticket,
  selected,
  onClick,
  selectable,
  checked,
  onCheckChange,
  onContextMenu,
  pinned,
}: Props) {
  const userProfile = useAuthStore((s) => s.user?.profile);
  // Fila cega: oculta a prévia da última mensagem pra não-admin em pendentes
  // de canal com hide_pending_content (a conversa é bloqueada no servidor).
  const contentHidden = isPendingContentHidden(
    ticket.whatsapp_number.hide_pending_content,
    ticket.status,
    userProfile,
  );
  // Texto deixado no compositor desta conversa (só em memória). Assina só o
  // próprio ticket — um rascunho novo em outra conversa não re-renderiza este.
  const rascunho = useRascunhos((s) => s.porTicket[ticket.id]?.texto);
  const rawChannelName =
    ticket.whatsapp_number.display_name ?? ticket.whatsapp_number.display_number ?? '';
  // Limite 11 chars no pill — depois disso, "…" (1 char unicode = mais compacto
  // que "..." 3 chars). Hover via `title` mostra o nome completo.
  const channelName =
    rawChannelName.length > 11 ? `${rawChannelName.slice(0, 11)}…` : rawChannelName;
  // Só aceita #RRGGBB — o sufixo de opacidade do fundo (`1f`) depende disso.
  const corDoDepartamento = /^#[0-9a-fA-F]{6}$/.test(ticket.department_color ?? '')
    ? ticket.department_color
    : null;

  // Faixa de 3px à esquerda: marcada no modo seleção (azul forte) >
  // selecionada/aberta (azul claro) > não lida (amarelo). Lido sem faixa
  // (rebrand, out/2026 — no lugar do cinza de antes).
  const corDaFaixa = checked
    ? 'bg-brand-500'
    : selected
      ? 'bg-brand-300'
      : ticket.unread_count > 0
        ? 'bg-warning-500'
        : null;

  return (
    // Lista "colada" estilo WhatsApp: sem caixa, sem sombra, sem espaço entre
    // os cards — só o divisor de 1px embaixo. Tom entre n-100 e n-200, puxado
    // pro claro (65/35) — dono, 17/out/2026: n-100 quase sumia, n-200 e o meio
    // escureceram demais; 80/20 ficou claro demais. Mistura dos tokens, não hex fixo — acompanha o tema escuro.
    <button
      type="button"
      onClick={onClick}
      onContextMenu={onContextMenu}
      className={cn(
        'relative flex w-full gap-[10px] border-[color-mix(in_srgb,var(--n-100)_65%,var(--n-200))] border-b px-[14px] py-[9px] text-left transition-colors duration-150',
        selected ? 'bg-brand-50' : 'bg-n-0 hover:bg-n-50',
        checked && 'bg-brand-100',
      )}
    >
      {corDaFaixa && (
        <span
          aria-hidden="true"
          className={cn('absolute top-0 bottom-0 left-0 w-[3px]', corDaFaixa)}
        />
      )}
      {selectable && (
        <span
          aria-label={checked ? 'Desmarcar' : 'Marcar'}
          onClick={(e) => {
            e.stopPropagation();
            onCheckChange?.();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              onCheckChange?.();
            }
          }}
          role="checkbox"
          aria-checked={Boolean(checked)}
          tabIndex={0}
          className={cn(
            'flex h-5 w-5 flex-shrink-0 cursor-pointer items-center justify-center self-center rounded border',
            checked
              ? 'border-brand-500 bg-brand-500 text-white'
              : 'border-n-300 bg-n-0 hover:border-brand-300',
          )}
        >
          {checked && <Check size={12} strokeWidth={3} />}
        </span>
      )}

      {/* Avatar com badge de não-lidas no canto superior direito. 44px de foto
          + 1px de folga + 1px de anel de cada lado = 48px visíveis (dono). */}
      <div className="relative flex-shrink-0 self-center">
        <div
          className="relative flex h-[44px] w-[44px] items-center justify-center overflow-hidden rounded-pill bg-n-50 text-n-400"
          // Anel na cor do canal: verde WhatsApp; rosa no Instagram.
          style={anelDoAvatar(ticket.whatsapp_number.channel_type === 'instagram')}
          title={ticket.whatsapp_number.channel_type === 'instagram' ? 'Instagram' : undefined}
        >
          {ticket.contact.profile_pic_url ? (
            <img
              src={ticket.contact.profile_pic_url}
              alt=""
              // Sem lazy, cada página de 50 tickets baixava as 50 fotos de uma
              // vez — incluindo as fora da tela. Apareceu no rastro: rolagem em
              // Fechados puxando ~10 fotos por página, muitas nunca vistas.
              // Com lazy, só desce a foto de card que entra no viewport.
              loading="lazy"
              className="h-full w-full object-cover"
              onError={(e) => {
                // Falha ao carregar (URL expirou ou objeto sumiu): esconde img,
                // ícone fica visível atrás (mesmo wrapper).
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : (
            <User size={23} strokeWidth={1.5} />
          )}
        </div>
        {ticket.unread_count > 0 && !selected && (
          <span
            className="-right-[2px] -top-[2px] absolute inline-flex h-[16px] min-w-[16px] items-center justify-center rounded-pill bg-danger-500 px-[4px] text-center font-bold text-[10px] text-white leading-none ring-2 ring-n-0"
            aria-label={`${ticket.unread_count} mensagens não lidas`}
          >
            {ticket.unread_count}
          </span>
        )}
      </div>

      {/* Body */}
      <div className="flex min-w-0 flex-1 flex-col gap-[3px] self-center">
        {/* Nome + etiquetas ao lado (bolinha na cor da tag, tooltip no hover —
            substitui os chips de texto que ficavam na linha dos pills). O nome
            trunca; as bolinhas não encolhem. */}
        <div className="flex items-center gap-1.5">
          <span className="min-w-0 truncate font-bold text-[13px] text-n-900 leading-tight">
            {ticket.contact.display_name ?? ticket.contact.identifier?.value ?? 'Sem nome'}
          </span>
          {ticket.tags.length > 0 && (
            <span className="flex flex-shrink-0 items-center gap-[4px]">
              {ticket.tags.slice(0, 3).map((t) => (
                <span
                  key={t.id}
                  className="inline-block h-[8px] w-[8px] rounded-pill"
                  style={{ background: t.color }}
                  title={t.name}
                  aria-label={`Etiqueta: ${t.name}`}
                />
              ))}
              {ticket.tags.length > 3 && (
                <span
                  className="font-semibold text-[10px] text-n-400"
                  title={ticket.tags
                    .slice(3)
                    .map((t) => t.name)
                    .join(', ')}
                >
                  +{ticket.tags.length - 3}
                </span>
              )}
            </span>
          )}
        </div>

        {/* Última mensagem (oculta na fila cega até aceitar). Com texto
            deixado no compositor, o card mostra o rascunho no lugar dela —
            igual ao WhatsApp — pra o atendente achar o que ficou pela metade. */}
        {contentHidden ? (
          <div className="flex items-center gap-1 text-[11px] italic text-n-400">
            <Lock size={11} strokeWidth={2} />
            Aceite para ver o conteúdo
          </div>
        ) : rascunho ? (
          <div className="flex min-w-0 items-center gap-1 text-[11px] italic text-n-500">
            <Pencil size={11} strokeWidth={2} className="flex-shrink-0" />
            <span className="truncate">
              <span className="font-bold text-n-600">Rascunho:</span> {rascunho}
            </span>
          </div>
        ) : (
          ticket.last_message_preview && (
            // Não lida: texto mais escuro (sem negrito — pedido do dono).
            <div
              className={cn(
                'truncate text-[11px]',
                ticket.unread_count > 0 ? 'text-n-900' : 'text-n-500',
              )}
            >
              {ticket.last_message_preview}
            </div>
          )
        )}

        {/* Badges: canal + departamento + agente — linha única, sem quebra.
            Mesma família nos três (formato suave, cantos 6px); só o
            departamento carrega cor própria. Opção "A" escolhida pelo dono no
            Claude Design (11/out/2026), sem os ícones que ela tinha. */}
        <div className="mt-px flex gap-[3px] overflow-hidden">
          {/* Canal / fila — azul-claro (agente fica no cinza). Truncado em 11
              chars + … no JS pra evitar dependência de maxWidth (que variava
              com font-rendering). */}
          <span
            className={cn(BADGE, 'flex-shrink-0 bg-brand-100 text-brand-700')}
            style={{ maxWidth: '100px' }}
            title={rawChannelName}
          >
            <span className="truncate">{channelName}</span>
          </span>

          {/* Departamento (quando houver) — na cor configurada do departamento,
              mesmo estilo do chip de etiqueta (TagsPopover) e das etiquetas em
              Contatos: fundo = cor a 12%, texto na cor cheia. Sem cor
              válida (card antigo do localStorage), fica no azul padrão. */}
          {ticket.department_name && (
            <span
              className={cn(
                BADGE,
                'flex-shrink-0',
                !corDoDepartamento && 'bg-brand-100 text-brand-700',
              )}
              style={{
                maxWidth: '100px',
                ...(corDoDepartamento && {
                  background: `${corDoDepartamento}1f`,
                  color: corDoDepartamento,
                }),
              }}
              title={ticket.department_name}
            >
              <span className="truncate">{ticket.department_name}</span>
            </span>
          )}

          {/* Etiquetas saíram daqui — agora são ícones coloridos ao lado do
              nome (cor da tag + tooltip). Vide bloco do nome acima. */}

          {/* Agente — último badge. Encolhe e trunca PRIMEIRO quando linha
              excede largura do card (em vez de ser cortado pelo overflow-hidden,
              o que destruía o canto arredondado). Mostra em active E closed —
              em closed o user_name vem do dono do ticket no momento do fechamento. */}
          {(ticket.status === 'active' || ticket.status === 'closed') && ticket.user_name && (
            <span
              className={cn(BADGE, 'min-w-0 flex-shrink bg-n-200 text-n-700')}
              style={{ maxWidth: '90px' }}
              title={ticket.user_name}
            >
              <span className="truncate">{ticket.user_name}</span>
            </span>
          )}
        </div>
      </div>

      {/* Meta direita (rebrand): horário 11px, protocolo 10,5px — mesmo gap
          de 10px do avatar↔corpo. Slots com altura fixa pra bot/transfer não
          "subirem" quando algum some. */}
      <div className="flex flex-shrink-0 flex-col items-end gap-0">
        <div className="mt-0 flex flex-col items-end gap-[3px] leading-none">
          {/* Slot tempo↔pin: fixada mostra o pin preenchido no lugar do
              horário (referência WhatsApp); fixar/desafixar é SÓ pelo menu de
              contexto (botão direito). */}
          <span className="flex h-[14px] items-center leading-none">
            {pinned ? (
              <span
                className="flex items-center text-n-500"
                title="Conversa fixada"
                aria-label="Conversa fixada"
              >
                <Pin size={12} strokeWidth={2} fill="currentColor" />
              </span>
            ) : (
              <span className="whitespace-nowrap text-[11px] text-n-500">
                {relativeTime(ticket.last_message_at)}
              </span>
            )}
          </span>
          <span className="flex h-[13px] items-center font-mono text-[10.5px] text-n-400 leading-none">
            {ticket.protocol_number ? `#${ticket.protocol_number}` : ''}
          </span>
          <Window24hCountdown
            channelType={ticket.whatsapp_number.channel_type}
            lastInboundAt={ticket.contact.last_inbound_at}
          />
        </div>
        {ticket.bot_active && (
          <span
            className="inline-flex h-[22px] w-[22px] items-center justify-center rounded-pill bg-brand-500 text-white"
            title="Atendido por chatbot"
            aria-label="Atendido por chatbot"
          >
            <Bot size={13} strokeWidth={1.75} />
          </span>
        )}
        {ticket.transferred_at && (
          <span
            className="inline-flex h-[22px] w-[22px] items-center justify-center rounded-pill bg-warning-500 text-white"
            title="Recebido por transferência"
            aria-label="Recebido por transferência"
          >
            <ArrowLeftRight size={13} strokeWidth={2} />
          </span>
        )}
      </div>
    </button>
  );
}

/**
 * Countdown ao vivo da janela de atendimento (regra Meta Cloud API).
 *
 * SEMPRE renderiza um <span> com `h-[12px]` (mesma altura dos outros slots
 * do trio) — slots vazios preservam altura, evitando bot/transfer "subirem"
 * quando o countdown some.
 *
 * 4 estados:
 *  1. Canal qr_code → vazio (não tem janela 24h)
 *  2. Cliente nunca respondeu → vazio (sem ponto de partida)
 *  3. Janela expirou → "00:00:00" vermelho (sinaliza fim do tempo)
 *  4. Janela ativa → "HH:MM:SS" vermelho ao vivo
 */
function Window24hCountdown({
  channelType,
  lastInboundAt,
}: {
  channelType?: 'meta_official' | 'qr_code' | 'instagram' | undefined;
  lastInboundAt: string | null;
}) {
  // Casos 1+2: sem janela aplicável → slot vazio (mantém altura).
  // useState/useEffect declarados condicionalmente seriam violação de hooks,
  // então o componente externo passa por aqui sem ativar o timer.
  if (channelType === 'qr_code' || !lastInboundAt) {
    return <span className="h-[12px] py-px font-mono text-[10px] leading-none" />;
  }

  return <ActiveWindow24hCountdown lastInboundAt={lastInboundAt} />;
}

function ActiveWindow24hCountdown({ lastInboundAt }: { lastInboundAt: string }) {
  const expiresAt = new Date(lastInboundAt).getTime() + TWENTY_FOUR_HOURS_MS;
  const [remainingMs, setRemainingMs] = useState(() => expiresAt - Date.now());

  useEffect(() => {
    setRemainingMs(expiresAt - Date.now());
    const tick = setInterval(() => {
      setRemainingMs(expiresAt - Date.now());
    }, 1000);
    return () => clearInterval(tick);
  }, [expiresAt]);

  // Caso 3: janela expirou → "expirado" vermelho (sinaliza claramente que
  // acabou; não confunde com countdown zerado de outras categorias).
  if (remainingMs <= 0) {
    return (
      <span
        className="flex h-[12px] items-center py-px font-medium text-[10px] text-danger-600 leading-none"
        title="Janela de atendimento (24h) expirou. Só template HSM aprovado é permitido até o cliente responder."
      >
        expirado
      </span>
    );
  }

  // Caso 4: janela ativa → countdown HH:MM:SS ao vivo.
  const total = Math.floor(remainingMs / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;

  return (
    <span
      className="flex h-[12px] items-center py-px font-mono font-medium text-[10px] text-danger-600 leading-none tabular-nums"
      title="Tempo restante da janela de atendimento (Cloud API). Cliente respondendo reseta pra 24h."
    >
      {String(h).padStart(2, '0')}:{String(m).padStart(2, '0')}:{String(s).padStart(2, '0')}
    </span>
  );
}
